import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import endpoints from "../../helpers/endpoints";
import fetch from "../../utils/fetch";
import { getAdminHeaders } from "../../utils/adminAuth";
import styles from "./ServicePackageManagement.module.scss";

type PlanAddon = {
  code: string;
  quantity: number;
  pricing?: Array<{
    value?: number;
    threshold?: number;
  }>;
  paymentThreshold?: number;
};

type ServicePlan = {
  id: string;
  code: string;
  service?: string;
  name: string;
  description?: string;
  price: number;
  currency: string;
  period?: string;
  addons: PlanAddon[];
  subscriberCount: number;
  activeSubscriberCount: number;
  pendingSubscriberCount: number;
  pastDueSubscriberCount: number;
  qrCodeLimits?: {
    dynamic?: number;
    static?: number;
  };
  emailLimits?: {
    monthly?: number;
    credit?: number;
  };
  metadata?: Record<string, any>;
};

type PlanDraft = {
  name: string;
  description: string;
  price: number;
  dynamicLimit: number;
  staticLimit: number;
  emailMonthlyLimit: number;
};

const dynamicAddonCode = "dynamic-code-storage";
const staticAddonCode = "static-code-storage";
const emailMonthlyAddonCode = "ninetick-email-monthly";

function getAddonQuantity(plan: ServicePlan, code: string) {
  const addon = plan.addons?.find((item) => item.code === code);
  return addon?.quantity || 0;
}

function toDraft(plan: ServicePlan): PlanDraft {
  return {
    name: plan.name || "",
    description: plan.description || "",
    price: Number(plan.price || 0),
    dynamicLimit: Number(plan.qrCodeLimits?.dynamic ?? getAddonQuantity(plan, dynamicAddonCode)),
    staticLimit: Number(plan.qrCodeLimits?.static ?? getAddonQuantity(plan, staticAddonCode)),
    emailMonthlyLimit: Number(plan.emailLimits?.monthly ?? plan.metadata?.emailMonthlyLimit ?? getAddonQuantity(plan, emailMonthlyAddonCode)),
  };
}

function updateAddonQuantity(addons: PlanAddon[], code: string, quantity: number) {
  const nextAddons = [...(addons || [])];
  const index = nextAddons.findIndex((item) => item.code === code);
  if (index >= 0) {
    nextAddons[index] = {
      ...nextAddons[index],
      quantity,
    };
    return nextAddons;
  }
  return [
    ...nextAddons,
    {
      code,
      quantity,
      pricing: [],
    },
  ];
}

function formatNumber(value?: number) {
  return Number(value || 0).toLocaleString("vi-VN");
}

const ServicePackageManagement = () => {
  const [plans, setPlans] = useState<ServicePlan[]>([]);
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>({});
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const totals = useMemo(() => {
    return plans.reduce(
      (obj, plan) => ({
        plans: obj.plans + 1,
        subscribers: obj.subscribers + (plan.subscriberCount || 0),
        activeSubscribers: obj.activeSubscribers + (plan.activeSubscriberCount || 0),
        qrLimit: obj.qrLimit + (getAddonQuantity(plan, dynamicAddonCode) || 0) + (getAddonQuantity(plan, staticAddonCode) || 0),
        emailLimit: obj.emailLimit + Number(plan.emailLimits?.monthly || 0),
      }),
      { plans: 0, subscribers: 0, activeSubscribers: 0, qrLimit: 0, emailLimit: 0 },
    );
  }, [plans]);

  async function loadPlans() {
    setLoading(true);
    setError("");
    try {
      const res: any = await fetch(endpoints.SERVICE_PACKAGE_SUMMARY, {
        headers: getAdminHeaders(),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không tải được danh sách gói dịch vụ.");
      }
      const result = Array.isArray(res?.body?.result) ? res.body.result : [];
      setPlans(result);
      setDrafts(result.reduce((obj: Record<string, PlanDraft>, plan: ServicePlan) => ({ ...obj, [plan.id]: toDraft(plan) }), {}));
    } catch (err: any) {
      setError(err?.message || "Không tải được danh sách gói dịch vụ.");
    } finally {
      setLoading(false);
    }
  }

  function updateDraft(planId: string, field: keyof PlanDraft, value: string) {
    setDrafts((current) => ({
      ...current,
      [planId]: {
        ...current[planId],
        [field]: field === "name" || field === "description" ? value : Math.max(Number(value || 0), 0),
      },
    }));
  }

  async function savePlan(plan: ServicePlan) {
    const draft = drafts[plan.id];
    if (!draft) return;

    setSavingId(plan.id);
    setError("");
    setMessage("");
    try {
      let addons = updateAddonQuantity(plan.addons, dynamicAddonCode, draft.dynamicLimit);
      addons = updateAddonQuantity(addons, staticAddonCode, draft.staticLimit);
      addons = updateAddonQuantity(addons, emailMonthlyAddonCode, draft.emailMonthlyLimit);

      const res: any = await fetch(`${endpoints.SERVICE_PACKAGES}/${encodeURIComponent(plan.id)}`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({
          name: draft.name.trim(),
          description: draft.description,
          price: draft.price,
          addons,
          metadata: {
            ...(plan.metadata || {}),
            emailMonthlyLimit: draft.emailMonthlyLimit,
          },
        }),
      });
      if (res?.status >= 400) {
        throw new Error(res?.body?.message || "Không lưu được gói dịch vụ.");
      }
      setMessage(`Đã cập nhật gói ${draft.name || plan.name}.`);
      await loadPlans();
    } catch (err: any) {
      setError(err?.message || "Không lưu được gói dịch vụ.");
    } finally {
      setSavingId("");
    }
  }

  useEffect(() => {
    loadPlans();
  }, []);

  return (
    <div className={styles.ContentWrapper}>
      <div className={styles.Header}>
        <div>
          <h1 className={styles.Title}>Quản lý gói dịch vụ</h1>
          <p className={styles.Subtitle}>Theo dõi số user đã mua và chỉnh giới hạn QR code/email của từng gói.</p>
        </div>
        {loading && <CircularProgress size={28} />}
      </div>

      {error && <div className={styles.Error}>{error}</div>}
      {message && <div className={styles.Success}>{message}</div>}

      <div className={styles.KpiGrid}>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Số gói</div>
          <div className={styles.KpiValue}>{formatNumber(totals.plans)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>User đã mua</div>
          <div className={styles.KpiValue}>{formatNumber(totals.subscribers)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>User đang active</div>
          <div className={styles.KpiValue}>{formatNumber(totals.activeSubscribers)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Tổng hạn mức QR</div>
          <div className={styles.KpiValue}>{formatNumber(totals.qrLimit)}</div>
        </div>
        <div className={styles.KpiCard}>
          <div className={styles.KpiLabel}>Tổng email/tháng</div>
          <div className={styles.KpiValue}>{formatNumber(totals.emailLimit)}</div>
        </div>
      </div>

      <div className={styles.Panel}>
        <div className={styles.Toolbar}>
          <h2 className={styles.PanelTitle}>Danh sách gói</h2>
          <Tooltip title="Tải lại dữ liệu">
            <span>
              <IconButton onClick={loadPlans} disabled={loading || Boolean(savingId)}>
                <RefreshIcon />
              </IconButton>
            </span>
          </Tooltip>
        </div>

        <div className={styles.TableWrap}>
          <Table>
            <TableHead sx={{ backgroundColor: "#F6F6F7" }}>
              <TableRow>
                <TableCell>Gói</TableCell>
                <TableCell>Service</TableCell>
                <TableCell align="right">User đã mua</TableCell>
                <TableCell align="right">Active</TableCell>
                <TableCell align="right">Giá</TableCell>
                <TableCell align="right">QR động</TableCell>
                <TableCell align="right">QR tĩnh</TableCell>
                <TableCell align="right">Email/tháng</TableCell>
                <TableCell>Mô tả</TableCell>
                <TableCell align="center">Lưu</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {plans.map((plan) => {
                const draft = drafts[plan.id] || toDraft(plan);
                return (
                  <TableRow key={plan.id}>
                    <TableCell>
                      <div className={styles.PlanName}>
                        <input className={styles.Input} value={draft.name} onChange={(event) => updateDraft(plan.id, "name", event.target.value)} />
                        <span className={styles.PlanCode}>{plan.code} · {plan.currency}</span>
                      </div>
                    </TableCell>
                    <TableCell className={styles.ReadonlyCell}>{plan.service || "qrcode"}</TableCell>
                    <TableCell align="right" className={styles.ReadonlyCell}>{formatNumber(plan.subscriberCount)}</TableCell>
                    <TableCell align="right" className={styles.ReadonlyCell}>{formatNumber(plan.activeSubscriberCount)}</TableCell>
                    <TableCell align="right">
                      <input className={styles.Input} type="number" value={draft.price} onChange={(event) => updateDraft(plan.id, "price", event.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <input className={styles.Input} type="number" value={draft.dynamicLimit} onChange={(event) => updateDraft(plan.id, "dynamicLimit", event.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <input className={styles.Input} type="number" value={draft.staticLimit} onChange={(event) => updateDraft(plan.id, "staticLimit", event.target.value)} />
                    </TableCell>
                    <TableCell align="right">
                      <input className={styles.Input} type="number" value={draft.emailMonthlyLimit} onChange={(event) => updateDraft(plan.id, "emailMonthlyLimit", event.target.value)} />
                    </TableCell>
                    <TableCell>
                      <input className={styles.Input} value={draft.description} onChange={(event) => updateDraft(plan.id, "description", event.target.value)} />
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="Lưu gói">
                        <span>
                          <IconButton color="primary" disabled={loading || savingId === plan.id} onClick={() => savePlan(plan)}>
                            {savingId === plan.id ? <CircularProgress size={20} /> : <SaveIcon />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && plans.length === 0 && <div className={styles.Muted}>Không có dữ liệu gói dịch vụ.</div>}
        </div>
      </div>
    </div>
  );
};

export default ServicePackageManagement;
